import React, { FC } from 'react';
import { useParams, useLocation, Outlet } from 'react-router';
import styled from 'styled-components';
import { BiPaint } from 'react-icons/bi';
import { CgTerminal } from 'react-icons/cg';
import { GrConnect, GrGraphQl } from "react-icons/gr";
import { MdOutlineSettings, MdOutlineSpaceDashboard, MdCompareArrows, MdHistory } from 'react-icons/md';

import { ExistingShopRouteState } from '../route_states/existing.shop.route.state';
import ExistingShopHeader from '../components/header/ExistingShopHeader';
import ExistingShopSidebar, { SidebarItem } from '../components/navigation/ExistingShopSidebar';
import Badge from '../components/header/Badge';
import HeaderShopSearch from '../components/header/HeaderShopSearch';
import FavouriteShopButton from '../components/favourite/FavouriteShopButton';
import { getCurrentStoreTitle, getCurrentStoreSecretKey } from '../cache/current.store.cache';
import { IoLanguage } from 'react-icons/io5';
import { MdAttachMoney } from "react-icons/md";
import { GiStairsGoal } from 'react-icons/gi';
import { FaShopify } from 'react-icons/fa';
import { isShopifyShop } from '../models/shopify';

const FITQUIZ_INTERNAL_LOGIN_URL = 'https://fitquiz.easysize.me/shopify/internal-authenticate/';

type ExistingShopSettingsProps = {
  id?: string;
};

const ExistingShopSettings: FC<ExistingShopSettingsProps> = () => {
  const params = useParams();
  const { id } = params;
  const location = useLocation();
  const state = location.state as ExistingShopRouteState;
  const shopName = state?.shop_name || getCurrentStoreTitle();
  const secretKey = state?.shop_key || getCurrentStoreSecretKey();

  const navMenuItems: SidebarItem[] = [
    { type: 'route', id: 'fitquiz', label: 'Fitquiz', icon: <BiPaint />, to: 'fitquiz' },
    { type: 'route', id: 'onboarding', label: 'Onboarding Steps', icon: <GiStairsGoal />, to: 'onboarding-steps' },
    { type: 'route', id: 'widget', label: 'Widget', icon: <CgTerminal />, to: 'widget' },
    { type: 'route', id: 'translations', label: 'Translations', icon: <IoLanguage />, to: 'translations' },
    { type: 'route', id: 'billing', label: 'Billing', icon: <MdAttachMoney />, to: 'billing' },
    { type: 'route', id: 'features', label: 'Available Features', icon: <MdOutlineSpaceDashboard />, to: 'available-features' },
    { type: 'route', id: 'size-mapping', label: 'Size Mapping', icon: <GrConnect />, to: 'size-mapping' },
    { type: 'route', id: 'size-conversion', label: 'Size Conversion', icon: <MdCompareArrows />, to: 'size-conversion' },
    { type: 'route', id: 'advanced', label: 'Advanced', icon: <MdOutlineSettings />, to: 'advanced-settings' },
    ...(isShopifyShop(shopName) ? [
      { type: 'route', id: 'shopify', label: 'Shopify', icon: <FaShopify />, to: 'shopify' } as SidebarItem,
      { type: 'route', id: 'shopify-graphql', label: 'Shopify GraphQL', icon: <GrGraphQl />, to: 'shopify-graphql' } as SidebarItem,
    ] : []),
    { type: 'route', id: 'history', label: 'History', icon: <MdHistory />, to: 'history' },
  ];

  return (
    <Container>
      <ExistingShopHeader
        shop_id={id}
        search={<HeaderShopSearch />}
        brand={() => (
          <>
            <s-heading fontSize='large'>{secretKey ? (
              // The secret key is a credential: POST it so it stays out of URLs and access logs.
              <FitquizLoginForm action={FITQUIZ_INTERNAL_LOGIN_URL} method='post' target='_blank' rel='noopener'>
                <input type='hidden' name='shop' value={String(secretKey)} />
                <FitquizLoginButton type='submit'>{shopName}</FitquizLoginButton>
              </FitquizLoginForm>
            ) : shopName}</s-heading>
            <Badge style={{ cursor: 'text'}} backgroundColor='#557dec'>
              <BadgeText>{id}</BadgeText>
            </Badge>
            {id && <FavouriteShopButton shopId={id} shopName={shopName || id} />}
          </>
        )}
      />
      <SettingsLayout>
        <ExistingShopSidebar items={navMenuItems} routeState={{ shop_name: shopName }} />
        <MainContent><Outlet /></MainContent>
      </SettingsLayout>
    </Container>
  );
};

export default ExistingShopSettings;

const Container = styled.div`
  width: 100%;
  min-width: 0;
`;

const SettingsLayout = styled.div`
  display: flex;
  align-items: stretch;
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  padding: 24px 16px;
  @media (max-width: 767px) { padding: 16px 8px; }
`;

const BadgeText = styled.span`
  color: white;
`;

const FitquizLoginForm = styled.form`
  display: inline;
`;

const FitquizLoginButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: var(--p-interactive, #2c6ecb);
  font: inherit;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
`;
